import React, { useState } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { Modal } from 'components/Modal';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { ProductSelect, ProductSelected } from 'components/Select/ProductSelect';
import { ProductState, TotadminPermission } from 'data/__generated__';

interface AddProductsToSupplierModalProps {
    isOpen: boolean;
    onClose: () => void;
    onAddProducts: ({ productIds }: { productIds: string[] }) => void;
    supplierName: string | null;
}

export const AddProductsToSupplierModal: React.FC<AddProductsToSupplierModalProps> = ({
    isOpen,
    onClose,
    onAddProducts,
    supplierName,
}) => {
    const [selectedProducts, setSelectedProducts] = useState<ProductSelected[]>([]);

    const handleAddProduct = (currentProduct: ProductSelected | null) => {
        if (currentProduct && !selectedProducts.some((p) => p._id === currentProduct._id)) {
            setSelectedProducts([...selectedProducts, currentProduct]);
        }
    };

    const handleRemoveProduct = (productId: string) => {
        setSelectedProducts(selectedProducts.filter((p) => p._id !== productId));
    };

    const handleAddProducts = () => {
        if (selectedProducts.length === 0) {
            toast.warning('Veuillez sélectionner au moins un produit');
            return;
        }

        const productIds = selectedProducts.map((product) => product._id);
        onAddProducts({ productIds });
        setSelectedProducts([]);
        onClose();
    };

    const handleClose = () => {
        setSelectedProducts([]);
        onClose();
    };

    return (
        <Modal isOpen={isOpen} onClose={handleClose} title="Ajouter des produits au fournisseur">
            <ModalContent>
                <SupplierInfo>
                    {supplierName && (
                        <SupplierName>
                            Fournisseur : <strong>{supplierName}</strong>
                        </SupplierName>
                    )}
                </SupplierInfo>

                <ProductSelectWrapper>
                    <ProductSelect
                        label="Ajouter un produit"
                        value={null}
                        onChange={handleAddProduct}
                        filters={{ states: [ProductState.Regular, ProductState.Created] }}
                        dataTest="product-select"
                    />
                </ProductSelectWrapper>

                <SelectedProductsList>
                    <SelectedProductsTitle>Produits sélectionnés ({selectedProducts.length})</SelectedProductsTitle>
                    {selectedProducts.length > 0 ? (
                        <ProductsList>
                            {selectedProducts.map((product) => (
                                <ProductItem key={product._id}>
                                    <ProductInfo>
                                        <ProductName>{product.fullname}</ProductName>
                                    </ProductInfo>
                                    <RemoveButton onClick={() => handleRemoveProduct(product._id)}>✕</RemoveButton>
                                </ProductItem>
                            ))}
                        </ProductsList>
                    ) : (
                        <NoProductsMessage>Aucun produit sélectionné</NoProductsMessage>
                    )}
                </SelectedProductsList>

                <ButtonsContainer>
                    <TotemPrimaryButton
                        onClick={handleAddProducts}
                        disabled={selectedProducts.length === 0}
                        $minWidth="200px"
                        requiredPermissions={[TotadminPermission.SupplyOrdersManage]}
                    >
                        Ajouter les produits
                    </TotemPrimaryButton>
                    <TotemPrimaryButton onClick={handleClose} $isSecondaryStyle $minWidth="200px">
                        Annuler
                    </TotemPrimaryButton>
                </ButtonsContainer>
            </ModalContent>
        </Modal>
    );
};

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px;
    min-width: 500px;
`;

const SupplierInfo = styled.div`
    margin-bottom: 20px;
`;

const SupplierName = styled.div`
    font-size: 16px;
    color: ${({ theme }) => theme.textColor};
`;

const ProductSelectWrapper = styled.div`
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const SelectedProductsList = styled.div`
    margin-bottom: 20px;
`;

const SelectedProductsTitle = styled.h4`
    font-size: 16px;
    font-weight: 600;
    margin-bottom: 10px;
    color: ${({ theme }) => theme.textColor};
`;

const ProductsList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
    max-height: 200px;
    overflow-y: auto;
`;

const ProductItem = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 8px 12px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
    border: 1px solid ${({ theme }) => theme.lightBorderColor};
    border-radius: 4px;
`;

const ProductInfo = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
`;

const ProductName = styled.span`
    font-weight: 500;
    color: ${({ theme }) => theme.textColor};
`;

const ProductBrand = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 14px;
`;

const ProductVolume = styled.span`
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 14px;
`;

const RemoveButton = styled.button`
    background: none;
    border: none;
    color: ${({ theme }) => theme.errorColor};
    cursor: pointer;
    font-size: 16px;
    padding: 4px;
    border-radius: 2px;

    &:hover {
        background-color: ${({ theme }) => theme.errorColor}20;
    }
`;

const NoProductsMessage = styled.div`
    text-align: center;
    color: ${({ theme }) => theme.infoTextColor};
    font-style: italic;
    padding: 20px;
`;

const ButtonsContainer = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 20px;
    gap: 20px;
`;
